// API Sessions Section
import { getSessions, cleanOtherSessions, removeSession } from '../user';
import { formatDisplayDateTime } from '../utils/dateFormat';

function ApiSessionsSection() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [currentTokenId, setCurrentTokenId] = useState('');

  useEffect(() => {
    fetchSessions();
    // Use token_id for current session detection
    const storedTokenId = localStorage.getItem('token_id');
    setCurrentTokenId(storedTokenId ? storedTokenId : '');
  }, []);

  async function fetchSessions() {
    setLoading(true);
    setError('');
    try {
      const data = await getSessions();
      let sessionsArr = [];
      if (Array.isArray(data)) {
        sessionsArr = data;
      } else if (data && Array.isArray(data.sessions)) {
        sessionsArr = data.sessions;
      } else {
        sessionsArr = [];
      }
      setSessions(sessionsArr);
    } catch (e) {
      setError('Failed to load sessions.');
      // Removed console.error for production cleanliness
    }
    setLoading(false);
  }

  async function handleRemoveSession(tokenId) {
    await removeSession(tokenId);
    setActionMsg('Session logged out.');
    fetchSessions();
  }

  async function handleCleanOthers() {
    await cleanOtherSessions();
    setActionMsg('All other sessions logged out.');
    fetchSessions();
  }

  if (loading) return <div>Loading sessions...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  // Convert currentTokenId to number for comparison if possible
  let currentTokenNum = currentTokenId;
  if (!isNaN(currentTokenId)) {
    currentTokenNum = Number(currentTokenId);
  }

  return (
    <div>
      {actionMsg && <div className="text-green-600 mb-2">{actionMsg}</div>}
      <button
        className="mb-4 inline-flex items-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
        onClick={handleCleanOthers}
      >
        Log out all other sessions
      </button>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
          {sessions.length === 0 ? (
            <tr><td colSpan="4" className="px-4 py-2 text-center text-gray-400">No active sessions.</td></tr>
          ) : (
            sessions.map((session) => {
              // Always compare as strings for reliability
              const isCurrent = String(session.token_id) === String(currentTokenId) || String(session.token_id) === String(currentTokenNum);
              return (
                <tr key={session.token_id} className={isCurrent ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''}>
                  <td className="px-4 py-2">{session.device || 'Unknown'}</td>
                  <td className="px-4 py-2">{session.ip || '-'}</td>
                  <td className="px-4 py-2">{formatDisplayDateTime(session.last_active) || '-'}</td>
                  <td className="px-4 py-2 text-right">
                    {isCurrent ? (
                      <span className="inline-block rounded bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-500 border border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 cursor-not-allowed">This session</span>
                    ) : (
                      <button
                        className="inline-flex items-center gap-2 rounded-lg border border-red-700 bg-red-700 px-3 py-1 text-xs font-semibold text-white hover:bg-red-600"
                        onClick={() => handleRemoveSession(session.token_id)}
                      >
                        Log out session
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
import React, { useEffect, useState } from 'react';
import api from '../api';
import { getUserInfo } from '../user';
import SecuredImage from '../components/SecuredImage';
import config from '../config';

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [pwForm, setPwForm] = useState({ password: '', password_new: '', password_new_confirm: '' });
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState('');

  // Use initials from user object if available
  function getInitials(u) {
    return u?.initials || '?';
  }

  useEffect(() => {
    getUserInfo().then((info) => {
      setUser(info);
      setForm({
        email: info?.email || '',
        name: info?.name || '',
        // notification prefs (example)
        email1: true,
        email2: true,
        email3: true,
        push: 'enable',
      });
      setPhotoPreview(info?.avatar || '');
      setLoading(false);
    });
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  }

  async function handleProfileSubmit(e) {
    e.preventDefault();
    setProfileSuccess(false);
    setProfileError('');
    try {
      const formData = new FormData();
      // Only send fields accepted by the API
      if (form.name) formData.append('name', form.name);
      if (form.email) formData.append('email', form.email);
      if (photoFile) formData.append('avatar', photoFile);
      // Debug: print all FormData entries and build a plain object for logging
      const debugPayload = {};
      for (let pair of formData.entries()) {
        debugPayload[pair[0]] = pair[1];
        console.log('FormData:', pair[0], pair[1]);
      }
      // Log the full payload as an object (file will show as File object)
      console.log('Posting to /me/update:', debugPayload);
      await api.post('/me/update', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Refresh user info after update
      const updatedInfo = await getUserInfo();
      setUser(updatedInfo);
      setForm({
        email: updatedInfo?.email || '',
        name: updatedInfo?.name || '',
        email1: true,
        email2: true,
        email3: true,
        push: 'enable',
      });
      setPhotoPreview(updatedInfo?.avatar || '');
      setProfileSuccess(true);
      setProfileError('');
    } catch (err) {
      setProfileError(err?.response?.data?.message || err?.response?.data?.errors?.avatar?.[0] || 'Failed to update profile.');
    }
  }

  function handlePwChange(e) {
    const { name, value } = e.target;
    setPwForm(f => ({ ...f, [name]: value }));
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setPwSuccess(false);
    setPwError('');
    const { password, password_new, password_new_confirm } = pwForm;
    if (!password || !password_new || !password_new_confirm) {
      setPwError('Please fill in all password fields.');
      return;
    }
    if (password_new !== password_new_confirm) {
      setPwError('New passwords do not match.');
      return;
    }
    try {
      await api.post('/me/change-password', {
        current_password: password,
        new_password: password_new,
        new_password_confirmation: password_new_confirm
      });
      setPwSuccess(true);
      setPwError('');
      setPwForm({ password: '', password_new: '', password_new_confirm: '' });
    } catch (err) {
      setPwError(err?.response?.data?.message || 'Failed to update password.');
    }
  }

  async function handleBillingSubmit(e) {
    e.preventDefault();
    // Implement billing update logic here
    setSuccess(true);
  }

  async function handleNotificationsSubmit(e) {
    e.preventDefault();
    // Implement notifications update logic here
    setSuccess(true);
  }

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-4 lg:space-y-8 dark:text-gray-100">
      {/* Banner */}
      <div className="relative rounded-lg bg-gradient-to-r from-emerald-400 to-purple-500 p-6 text-center overflow-hidden" style={{ minHeight: '180px' }}>
        {/* Banner image if present */}
        <img
          src="/profile-header.png"
          alt="Profile Banner"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
          style={{ zIndex: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
        <div className="relative z-10 mb-5 inline-block">
          <div className="rounded-full bg-white/50 p-2">
            {photoPreview ? (
              photoPreview.startsWith('blob:') ? (
                <img
                  src={photoPreview}
                  alt="User Avatar"
                  className="inline-block size-20 rounded-full object-cover"
                />
              ) : (
                <SecuredImage
                  src={photoPreview}
                  alt="User Avatar"
                  className="inline-block size-20 rounded-full"
                  fallback={
                    <div className="inline-flex size-20 items-center justify-center rounded-full bg-emerald-700 text-2xl font-bold text-white">
                      {getInitials(user)}
                    </div>
                  }
                />
              )
            ) : (
              <div className="inline-flex size-20 items-center justify-center rounded-full bg-emerald-700 text-2xl font-bold text-white">
                {getInitials(user)}
              </div>
            )}
          </div>
        </div>
        <h3 className="relative z-10 text-xl font-bold text-white">{form.name || user?.email || 'User'}</h3>
        <h4 className="relative z-10 text-sm font-medium text-white/90">&nbsp;</h4>
      </div>
      {/* END Banner */}

      {/* Vital Info */}
      <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-xs dark:bg-gray-800 dark:text-gray-100">
        <div className="grow p-5 md:flex lg:p-8">
          <div className="mb-5 border-b border-gray-200 md:mb-0 md:w-1/3 md:flex-none md:border-0 dark:border-gray-700">
            <h3 className="mb-1 flex items-center justify-start gap-2 font-semibold">
              <svg className="hi-mini hi-user-circle inline-block size-5 text-emerald-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-5.5-2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM10 12a5.99 5.99 0 00-4.793 2.39A6.483 6.483 0 0010 16.5a6.483 6.483 0 004.793-2.11A5.99 5.99 0 0010 12z" clipRule="evenodd" /></svg>
              <span>User Profile</span>
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">Your account’s vital info. Only your photo and email will be publicly visible.</p>
          </div>
          <div className="md:w-2/3 md:pl-24">
            <form className="space-y-6 xl:w-2/3" encType="multipart/form-data" onSubmit={handleProfileSubmit}>
              <div className="space-y-1">
                <label className="inline-block font-medium">Photo</label>
                <div className="space-y-4 sm:flex sm:items-center sm:gap-4 sm:space-y-0">
                  <div className="inline-flex size-16 items-center justify-center rounded-full bg-gray-100 text-gray-300 dark:bg-gray-700 dark:text-gray-500 overflow-hidden">
                    {photoPreview ? (
                      photoPreview.startsWith('blob:') ? (
                        <img
                          src={photoPreview}
                          alt="User Avatar"
                          className="inline-block size-16 rounded-full object-cover"
                        />
                      ) : (
                        <SecuredImage
                          src={photoPreview}
                          alt="User Avatar"
                          className="inline-block size-16 rounded-full"
                          fallback={<span className="text-lg font-bold">{getInitials(user)}</span>}
                        />
                      )
                    ) : (
                      <span className="text-lg font-bold">{getInitials(user)}</span>
                    )}
                  </div>
                  <label className="block">
                    <span className="sr-only">Choose profile photo</span>
                    <input type="file" id="photo" name="photo" className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-sm file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-emerald-700 hover:file:bg-emerald-100 dark:text-gray-400 dark:file:bg-emerald-200 dark:file:text-emerald-800 dark:hover:file:bg-emerald-300" onChange={handlePhotoChange} />
                  </label>
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="email" className="inline-block font-medium">Email</label>
                <input type="email" id="email" name="email" value={form.email} onChange={handleChange} placeholder="john.doe@example.com" className="block w-full rounded-lg border border-gray-200 px-3 py-2 leading-6 placeholder-gray-500 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/50 dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 dark:focus:border-emerald-500" />
              </div>
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2 text-sm leading-5 font-semibold text-white hover:border-emerald-600 hover:bg-emerald-600 hover:text-white focus:ring-3 focus:ring-emerald-400/50 active:border-emerald-700 active:bg-emerald-700 dark:focus:ring-emerald-400/90">Update Profile</button>
              {profileSuccess && <div className="text-green-600">Profile updated!</div>}
              {profileError && <div className="text-red-600">{profileError}</div>}
            </form>
          </div>
        </div>
      </div>
      {/* END Vital Info */}
      {/* Change Password */}
      <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-xs dark:bg-gray-800 dark:text-gray-100">
        <div className="grow p-5 md:flex lg:p-8">
          <div className="mb-5 border-b border-gray-200 md:mb-0 md:w-1/3 md:flex-none md:border-0 dark:border-gray-700">
            <h3 className="mb-1 flex items-center justify-start gap-2 font-semibold">
              <svg className="hi-mini hi-lock-closed inline-block size-5 text-emerald-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" /></svg>
              <span>Change Password</span>
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">Changing your sign in password is an easy way to keep your account secure.</p>
          </div>
          <div className="md:w-2/3 md:pl-24">
            <form className="space-y-6 xl:w-2/3" onSubmit={handlePasswordSubmit}>
              <div className="space-y-1">
                <label htmlFor="password" className="inline-block font-medium">Current Password</label>
                <input type="password" id="password" name="password" value={pwForm.password} onChange={handlePwChange} className="block w-full rounded-lg border border-gray-200 px-3 py-2 leading-6 placeholder-gray-500 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/50 dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 dark:focus:border-emerald-500" />
              </div>
              <div className="space-y-1">
                <label htmlFor="password_new" className="inline-block font-medium">New Password</label>
                <input type="password" id="password_new" name="password_new" value={pwForm.password_new} onChange={handlePwChange} className="block w-full rounded-lg border border-gray-200 px-3 py-2 leading-6 placeholder-gray-500 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/50 dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 dark:focus:border-emerald-500" />
              </div>
              <div className="space-y-1">
                <label htmlFor="password_new_confirm" className="inline-block font-medium">Confirm Password</label>
                <input type="password" id="password_new_confirm" name="password_new_confirm" value={pwForm.password_new_confirm} onChange={handlePwChange} className="block w-full rounded-lg border border-gray-200 px-3 py-2 leading-6 placeholder-gray-500 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/50 dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 dark:focus:border-emerald-500" />
              </div>
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2 text-sm leading-5 font-semibold text-white hover:border-emerald-600 hover:bg-emerald-600 hover:text-white focus:ring-3 focus:ring-emerald-400/50 active:border-emerald-700 active:bg-emerald-700 dark:focus:ring-emerald-400/90">Update Password</button>
              {pwSuccess && <div className="text-green-600">Password updated!</div>}
              {pwError && <div className="text-red-600">{pwError}</div>}
            </form>
          </div>
        </div>
      </div>
      {/* END Change Password */}
      {/* Roles (read-only) */}
      <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-xs dark:bg-gray-800 dark:text-gray-100">
        <div className="grow p-5 md:flex lg:p-8">
          <div className="mb-5 border-b border-gray-200 md:mb-0 md:w-1/3 md:flex-none md:border-0 dark:border-gray-700">
            <h3 className="mb-1 flex items-center justify-start gap-2 font-semibold">
              <svg className="hi-mini hi-identification inline-block size-5 text-emerald-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M2 5a3 3 0 013-3h10a3 3 0 013 3v10a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V5a1 1 0 00-1-1H5zm5 3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a4 4 0 00-3.464 2H13.46A4 4 0 008 11z" clipRule="evenodd" /></svg>
              <span>User Roles</span>
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">Your roles determine your access in the system.</p>
          </div>
          <div className="md:w-2/3 md:pl-24 flex items-center">
            <ul className="flex flex-wrap gap-2">
              {user && Array.isArray(user.roles) && user.roles.length > 0 ? (
                user.roles.map(role => (
                  <li key={role} className="inline-block rounded bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-700">{role}</li>
                ))
              ) : (
                <li className="text-gray-400">No roles assigned</li>
              )}
            </ul>
          </div>
        </div>
      </div>
      {/* END Roles */}
      {/* Current API Sessions */}
      <div className="space-y-4 lg:space-y-8 dark:text-gray-100">
        <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-xs dark:bg-gray-800 dark:text-gray-100">
          <div className="grow p-5 md:flex lg:p-8">
            <div className="mb-5 border-b border-gray-200 md:mb-0 md:w-1/3 md:flex-none md:border-0 dark:border-gray-700">
              <h3 className="mb-1 flex items-center justify-start gap-2 font-semibold">
                <svg className="hi-mini hi-key inline-block size-5 text-emerald-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M17.707 12.293a1 1 0 00-1.414 0l-1.293 1.293-2.293-2.293a6 6 0 10-1.414 1.414l2.293 2.293-1.293 1.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414zM8 14a4 4 0 110-8 4 4 0 010 8z" /></svg>
                <span>Active API Sessions</span>
              </h3>
              <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                Below is a list of your current API sessions. Each session represents a device or application that has authenticated with your account.
              </p>
            </div>
            <div className="md:w-2/3 md:pl-24">
              <ApiSessionsSection />
            </div>
          </div>
        </div>
      </div>
      {/* END Current API Sessions */}
    </div>
  );
}
